import type { TranslatedString } from '@interfaces/shared';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { Typography } from 'hephaestus/web';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../hooks/useLocalization';
import {
  HouseholdModelPortfolioDraftSkeleton,
  HouseholdModelPortfolioDraftUnavailable,
} from '../householdModelPortfolioDraft/householdModelPortfolioDraftStates';
import { ReviewAccountAllocationsBlocked } from './reviewAccountAllocationsBlocked';
import { ReviewAccountAllocationsCancel } from './reviewAccountAllocationsCancel';
import { ReviewAccountAllocationsComparison } from './reviewAccountAllocationsComparison';
import { useReviewAccountAllocations } from './useReviewAccountAllocations';

const TEST_ID = 'review-account-allocations';

type ReviewAccountAllocationsOptions = {
  title?: TranslatedString;
  subtitle?: TranslatedString;
  approveButton?: TranslatedString;
  enableCancel?: boolean;
  cancelButton?: TranslatedString;
};

export type ReviewAccountAllocationsProp = {
  options: ReviewAccountAllocationsOptions;
  clientGroupId?: string;
  onNext: (branchId?: string) => void | Promise<void>;
  stepLoading?: boolean;
  workflowCompletionId?: string;
  onWorkflowCancelled?: () => void;
};

// Read-only: Back is the workflow shell's button, and Approve hands the staged
// matrix on to the form sequence untouched.
export const ReviewAccountAllocations = ({
  options,
  clientGroupId,
  onNext,
  stepLoading = false,
  workflowCompletionId,
  onWorkflowCancelled,
}: ReviewAccountAllocationsProp) => {
  const { translateBackendString } = useLocalization();
  const { workflowData } = useContext(WorkflowContext);
  const { t } = useTranslation('workflowCompletions');

  const householdClientGroupId =
    workflowData.currentHouseholdClientGroupId ?? clientGroupId;
  const { report, decision, loading, error, retry, approve } =
    useReviewAccountAllocations(householdClientGroupId);

  const title = translateBackendString(options?.title);
  const canCancel =
    !!options?.enableCancel && workflowCompletionId !== undefined;

  if (loading || stepLoading) {
    return (
      <HouseholdModelPortfolioDraftSkeleton title={title} testId={TEST_ID} />
    );
  }

  if (error || !report || !decision) {
    return (
      <HouseholdModelPortfolioDraftUnavailable
        title={title}
        message={t('reviewAccountAllocations.unavailable')}
        testId={TEST_ID}
        onRetry={error ? retry : undefined}
      />
    );
  }

  return (
    <WorkflowCompletionForm
      // Returns the approval's promise so the form holds Approve disabled until
      // the workflow has moved. approve decides again on a fresh report, so a
      // submit that bypasses the disabled button can't advance a blocked review.
      onSubmit={() => approve(onNext)}
      testId={`${TEST_ID}-form`}
      submitButtonProps={{
        label:
          translateBackendString(options?.approveButton) ||
          t('reviewAccountAllocations.approve'),
        disabled: decision.status !== 'REVIEWABLE',
        dataTestId: `${TEST_ID}-approve-button`,
      }}
      buttonFooter={
        canCancel &&
        workflowCompletionId !== undefined && (
          <ReviewAccountAllocationsCancel
            label={
              translateBackendString(options.cancelButton) || t('shared:cancel')
            }
            workflowCompletionId={workflowCompletionId}
            onWorkflowCancelled={onWorkflowCancelled}
          />
        )
      }
    >
      <Typography
        variant='displayLarge'
        sx={{ mt: 1, mb: options?.subtitle ? 0 : 2 }}
      >
        {title}
      </Typography>
      {options?.subtitle && (
        <Typography variant='bodyLarge' sx={{ mb: 3 }}>
          <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
            {translateBackendString(options.subtitle)}
          </ReactMarkdown>
        </Typography>
      )}
      {decision.status === 'BLOCKED' ? (
        <ReviewAccountAllocationsBlocked
          decision={decision}
          report={report}
          canCancel={canCancel}
          testId={TEST_ID}
        />
      ) : (
        <ReviewAccountAllocationsComparison
          report={report}
          comparison={decision.comparison}
          testId={TEST_ID}
        />
      )}
    </WorkflowCompletionForm>
  );
};
